/**
 * Stage 7: Payment
 *
 * Stripe Payment Element for completing the booking.
 * Uses Stripe.js for secure, PCI-compliant card collection.
 *
 * When a bookingId is available in bookingData, this component:
 * 1. Fetches Stripe publishable key from /api/v1/checkout/config
 * 2. Creates a PaymentIntent via POST /api/v1/bookings/:id/checkout
 * 3. Renders the Stripe Payment Element with the clientSecret
 * 4. Confirms payment via stripe.confirmPayment()
 *
 * When no bookingId is available (booking not yet created),
 * falls back to placeholder UI.
 *
 * Location: src/components/booking/flow/stages/Stage7Payment.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 * Story: SCRUM-567 - Stripe Payment Flow
 */

import { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import StripeProvider from '../../../../providers/StripeProvider';
import * as api from '../../../../utils/apiClient';
import Alert from '../../../common/Alert';
import { useCart } from '../../../../contexts/CartContext';

function isConsecutiveBookingEnabled() {
  return import.meta.env.VITE_FEATURE_CONSECUTIVE_BOOKING === 'true';
}

/**
 * Inner payment form with access to Stripe hooks.
 * Must be rendered inside a StripeProvider / Elements context.
 */
function StripePaymentForm({ amountBreakdown, bookingId, onNext, onBack, trackProgressAction, loading: parentLoading, returnUrlParam }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setPaymentError(null);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/booking/confirmation?${returnUrlParam || 'bookingId'}=${bookingId}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setPaymentError(error.message);
        setSubmitting(false);
        return;
      }

      // Payment succeeded without redirect (no 3DS required)
      if (trackProgressAction) {
        trackProgressAction(7, 'step_advanced');
      }

      onNext({
        paymentCompleted: true,
        paymentMethod: 'stripe',
        paymentAmount: amountBreakdown.totalCents,
      });
    } catch (err) {
      setPaymentError(
        typeof err?.message === 'string' && err.message
          ? err.message
          : 'Payment failed. Please try again.'
      );
      setSubmitting(false);
    }
  };

  const formatPrice = (cents) => {
    if (!cents) {
      return '$0.00';
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Fee Breakdown */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
        <div className="flex justify-between text-sm text-emerald-800">
          <span>Trip price</span>
          <span>{formatPrice(amountBreakdown.baseCents)}</span>
        </div>
        <div className="flex justify-between text-sm text-emerald-800">
          <span>Service fee</span>
          <span>{formatPrice(amountBreakdown.platformFeeCents)}</span>
        </div>
        <div className="flex justify-between font-bold text-emerald-900 border-t border-emerald-300 pt-2">
          <span>Total Due Today</span>
          <span className="text-xl">{formatPrice(amountBreakdown.totalCents)}</span>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div className="min-h-[200px]">
        <PaymentElement />
      </div>

      {/* Security Badges */}
      <div className="flex items-center justify-center gap-6 py-2 text-xs text-gray-500">
        <span>SSL Secured</span>
        <span>PCI Compliant</span>
        <span>Powered by Stripe</span>
      </div>

      {/* Payment Error */}
      {paymentError && (
        <Alert
          variant="error"
          message={typeof paymentError === 'string' ? paymentError : 'Payment could not be processed. Please try again.'}
          className="p-3"
        />
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4 pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting || parentLoading}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting || parentLoading}
          className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300"
        >
          {submitting ? 'Processing Payment...' : `Pay ${formatPrice(amountBreakdown.totalCents)}`}
        </button>
      </div>
    </form>
  );
}

/**
 * Stage7Payment Component
 *
 * @param {Object} props
 * @param {Object} props.bookingData - Current booking form data
 * @param {Function} props.onNext - Handler for proceeding to confirmation
 * @param {Function} props.onBack - Handler for going back
 * @param {Function} props.trackProgressAction - Handler for tracking progress
 * @param {number} props.totalPrice - Total price in cents
 */
export default function Stage7Payment({ bookingData, onNext, onBack, trackProgressAction, totalPrice }) {
  const bookingId = bookingData?.bookingId;
  const cart = useCart();
  const isCartCheckout = isConsecutiveBookingEnabled() && cart.items.length > 0 && cart.status === 'active';

  // SCRUM-709: Cart checkout path — PlaceholderPayment NOT triggered (AC-09)
  if (isCartCheckout) {
    return (
      <CartCheckout
        cart={cart}
        totalPrice={totalPrice}
        onNext={onNext}
        onBack={onBack}
        trackProgressAction={trackProgressAction}
      />
    );
  }

  // If no bookingId, use placeholder flow
  // (booking creation happens before checkout in the full flow)
  if (!bookingId) {
    return (
      <PlaceholderPayment
        totalPrice={totalPrice}
        onNext={onNext}
        onBack={onBack}
        trackProgressAction={trackProgressAction}
      />
    );
  }

  return (
    <StripeCheckout
      bookingId={bookingId}
      totalPrice={totalPrice}
      onNext={onNext}
      onBack={onBack}
      trackProgressAction={trackProgressAction}
    />
  );
}

/**
 * Real Stripe checkout flow.
 * Fetches config and creates PaymentIntent, then renders Payment Element.
 */
function StripeCheckout({ bookingId, onNext, onBack, trackProgressAction }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [amountBreakdown, setAmountBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function initCheckout() {
      try {
        // 1. Get Stripe publishable key
        const configRes = await api.get('checkout/config');
        if (cancelled) {
          return;
        }

        if (!configRes.success) {
          throw new Error('Failed to load payment configuration');
        }

        // 2. Create PaymentIntent
        const checkoutRes = await api.post(`bookings/${bookingId}/checkout`);
        if (cancelled) {
          return;
        }

        if (!checkoutRes.success) {
          const rawMsg = checkoutRes.error?.message;
          throw new Error(
            typeof rawMsg === 'string' && rawMsg
              ? rawMsg
              : typeof checkoutRes.error === 'string' && checkoutRes.error
              ? checkoutRes.error
              : 'Checkout failed'
          );
        }

        setPublishableKey(configRes.data.publishableKey);
        setClientSecret(checkoutRes.data.clientSecret);
        setAmountBreakdown(checkoutRes.data.amount);
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err?.message === 'string' && err.message
              ? err.message
              : typeof err?.error === 'string' && err.error
              ? err.error
              : 'Payment could not be processed. Please try again.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    initCheckout();

    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-2/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payment Error</h2>
          <p className="text-gray-600 mt-1">
            {typeof error === 'string' ? error : 'Payment could not be processed. Please try again.'}
          </p>
        </div>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret || !publishableKey) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Complete Your Booking</h2>
        <p className="text-gray-600 mt-1">Secure payment powered by Stripe</p>
      </div>

      <StripeProvider publishableKey={publishableKey} clientSecret={clientSecret}>
        <StripePaymentForm
          amountBreakdown={amountBreakdown}
          bookingId={bookingId}
          onNext={onNext}
          onBack={onBack}
          trackProgressAction={trackProgressAction}
          loading={loading}
        />
      </StripeProvider>
    </div>
  );
}

/**
 * SCRUM-709: Cart checkout flow.
 * Fetches config and creates PaymentIntent via POST /checkout/cart, then renders Payment Element.
 */
function CartCheckout({ cart, onNext, onBack, trackProgressAction }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [amountBreakdown, setAmountBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function initCartCheckout() {
      try {
        // 1. Get Stripe publishable key
        const configRes = await api.get('checkout/config');
        if (cancelled) { return; }

        if (!configRes.success) {
          throw new Error('Failed to load payment configuration');
        }

        // 2. Create PaymentIntent via cart checkout endpoint
        const checkoutRes = await api.post('checkout/cart', {
          cartId: cart.cartId,
          guideId: cart.guideId,
          experienceId: cart.experienceId,
          items: cart.items,
          holdId: cart.holdId,
        });
        if (cancelled) { return; }

        if (!checkoutRes.success) {
          const rawMsg = checkoutRes.error?.message;
          throw new Error(
            typeof rawMsg === 'string' && rawMsg
              ? rawMsg
              : typeof checkoutRes.error === 'string' && checkoutRes.error
              ? checkoutRes.error
              : 'Cart checkout failed'
          );
        }

        // Store cartId from response if returned
        if (checkoutRes.data.cartId) {
          cart.setCartId(checkoutRes.data.cartId);
        }

        setPublishableKey(configRes.data.publishableKey);
        setClientSecret(checkoutRes.data.clientSecret);
        setAmountBreakdown(checkoutRes.data.amount || {
          baseCents: cart.totals.subtotalCents,
          platformFeeCents: 0,
          totalCents: cart.totals.subtotalCents,
        });
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err?.message === 'string' && err.message
              ? err.message
              : typeof err?.error === 'string' && err.error
              ? err.error
              : 'Payment could not be processed. Please try again.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    initCartCheckout();

    return () => { cancelled = true; };
  }, [cart]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-2/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payment Error</h2>
          <p className="text-gray-600 mt-1">
            {typeof error === 'string' ? error : 'Payment could not be processed. Please try again.'}
          </p>
        </div>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret || !publishableKey) {
    return null;
  }

  const cartId = cart.cartId || cart.getCart().cartId;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Complete Your Booking</h2>
        <p className="text-gray-600 mt-1">Secure payment powered by Stripe</p>
      </div>

      <StripeProvider publishableKey={publishableKey} clientSecret={clientSecret}>
        <StripePaymentForm
          amountBreakdown={amountBreakdown}
          bookingId={cartId}
          returnUrlParam="cartId"
          onNext={(stageData) => {
            // SCRUM-709: Pass cartId for confirmation page navigation (AC-10)
            onNext({ ...stageData, cartId });
          }}
          onBack={onBack}
          trackProgressAction={trackProgressAction}
          loading={loading}
        />
      </StripeProvider>
    </div>
  );
}

/**
 * Placeholder payment UI for when bookingId is not available.
 * Preserves the existing flow until booking creation is wired to Stage 7.
 */
function PlaceholderPayment({ totalPrice, onNext, onBack, trackProgressAction }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const formatPrice = (cents) => {
    if (!cents) {
      return '$0.00';
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (trackProgressAction) {
        trackProgressAction(7, 'step_advanced');
      }

      const confirmationNumber = `OG${Date.now().toString().slice(-8)}`;

      onNext({
        paymentCompleted: true,
        confirmationNumber,
        paymentMethod: 'card',
        paymentAmount: totalPrice,
      });
    } catch (err) {
      setErrors({ submit: err.message || 'Payment failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Complete Your Booking</h2>
          <p className="text-gray-600 mt-1">Secure payment powered by Stripe</p>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex justify-between items-center">
          <span className="font-medium text-emerald-900">Total Due Today</span>
          <span className="text-2xl font-bold text-emerald-700">{formatPrice(totalPrice)}</span>
        </div>

        <Alert variant="warning" className="p-3">
          <p className="text-xs">
            <strong>Note:</strong> Stripe payment integration is being connected.
            This booking will use the placeholder flow.
          </p>
        </Alert>

        <div className="flex items-center justify-center gap-6 py-2 text-xs text-gray-500">
          <span>SSL Secured</span>
          <span>PCI Compliant</span>
          <span>Powered by Stripe</span>
        </div>

        {errors.submit && (
          <Alert variant="error" message={errors.submit} className="p-3" />
        )}

        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300"
          >
            {loading ? 'Processing Payment...' : 'Complete Booking'}
          </button>
        </div>
      </form>
    </div>
  );
}
